import { motion } from "framer-motion";
import { Code2, Smartphone, PenTool, Film, CloudCog, Sparkles } from "lucide-react";
import { SERVICES } from "@/constants/testIds";

const SVC = [
  {
    slug: "web-dev",
    icon: Code2,
    label: "Web Development",
    body: "Custom, high-performance web apps built with modern frameworks. Type-safe, testable, ship-ready.",
    span: "md:col-span-7 md:row-span-2",
    accent: true,
  },
  {
    slug: "mobile",
    icon: Smartphone,
    label: "Mobile Apps",
    body: "Native-feel iOS and Android products with pixel discipline.",
    span: "md:col-span-5",
  },
  {
    slug: "ux",
    icon: PenTool,
    label: "UX & Product Design",
    body: "Research, systems and interaction design that respects your users' time.",
    span: "md:col-span-5",
  },
  {
    slug: "video",
    icon: Film,
    label: "Video & Motion",
    body: "Editorial, cinematic edits and motion graphics that make your story travel.",
    span: "md:col-span-4",
  },
  {
    slug: "cloud",
    icon: CloudCog,
    label: "Cloud & DevOps",
    body: "Scalable cloud architectures, CI/CD, and observability from day one.",
    span: "md:col-span-4",
  },
  {
    slug: "ai",
    icon: Sparkles,
    label: "AI Product Engineering",
    body: "LLM features, RAG, agents — designed like products, not demos.",
    span: "md:col-span-4",
  },
];

export const Services = () => {
  return (
    <section
      id="services"
      data-testid={SERVICES.section}
      className="relative py-28 md:py-40"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20"
        >
          <div>
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-emerald-300/80 mb-5">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400" />
              What we do
            </div>
            <h2 className="font-display font-bold text-white text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[0.98] max-w-2xl">
              Six disciplines. <br />
              <span className="italic font-normal text-emerald-300">One senior team.</span>
            </h2>
          </div>
          <p className="text-white/60 max-w-md text-base md:text-lg">
            End-to-end capability across product, code, motion, and infrastructure — brought together by one accountable team.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6 auto-rows-[minmax(220px,auto)]">
          {SVC.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.slug}
                data-testid={SERVICES.card(s.slug)}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: i * 0.05, ease: [0.22, 0.61, 0.36, 1] }}
                className={`group relative overflow-hidden rounded-3xl p-8 md:p-10 border border-white/[0.06] ${
                  s.accent
                    ? "bg-gradient-to-br from-emerald-500/15 via-emerald-500/[0.06] to-transparent"
                    : "bg-[#09120E]/70"
                } ${s.span || ""}`}
              >
                {/* Glow blob */}
                <div
                  aria-hidden
                  className="absolute -right-16 -bottom-16 w-56 h-56 opacity-40 group-hover:opacity-70 transition-opacity duration-700"
                  style={{
                    background:
                      "radial-gradient(closest-side, rgba(0,208,132,0.55), transparent 70%)",
                    filter: "blur(30px)",
                    borderRadius: "50% 40% 60% 40%",
                  }}
                />
                <div className="relative flex items-start justify-between mb-8">
                  <div className="w-12 h-12 rounded-2xl bg-emerald-400/10 border border-emerald-400/25 grid place-items-center text-emerald-300">
                    <Icon size={22} strokeWidth={1.6} />
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.24em] text-white/30">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="relative font-display font-bold text-white text-xl md:text-2xl mb-3">
                  {s.label}
                </h3>
                <p className="relative text-white/60 text-sm md:text-[15px] leading-relaxed max-w-md">
                  {s.body}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
